/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, ClipboardList, FileSignature, CheckCircle2 } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError, useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import PracticeManagerLink from "@/components/PracticeManagerLink";
import { DOC_TYPES, REQUEST_CATEGORIES, labelFor, formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";

export default function FirmRequests() {
    const { tenant } = useAuth();
    const [requests, setRequests] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const [rs, ts, cs] = await Promise.all([
                api.get("/requests/"),
                api.get("/requests/templates"),
                api.get("/clients/"),
            ]);
            setRequests(rs.data);
            setTemplates(ts.data);
            setClients(cs.data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, []);

    const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

    async function setStatus(req, status) {
        try {
            await api.patch(`/requests/${req.id}/status`, null, { params: { status } });
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    return (
        <div>
            <PageHeader
                eyebrow="Requests"
                title="Document requests"
                description="Replace scattered email asks with structured, trackable document requests. Build templates once, send them in two clicks."
                actions={<NewRequestDialog templates={templates} clients={clients} onCreated={load} />}
                testId="requests-header"
            />

            <Tabs defaultValue="open">
                <TabsList data-testid="requests-tabs">
                    <TabsTrigger value="open" data-testid="reqs-tab-open">Open</TabsTrigger>
                    <TabsTrigger value="completed" data-testid="reqs-tab-completed">Completed</TabsTrigger>
                    <TabsTrigger value="all" data-testid="reqs-tab-all">All</TabsTrigger>
                    <TabsTrigger value="templates" data-testid="reqs-tab-templates">Templates ({templates.length})</TabsTrigger>
                </TabsList>

                {["open", "completed", "all"].map((tab) => (
                    <TabsContent key={tab} value={tab} className="mt-4">
                        {loading ? <div className="text-sm text-[#5E636E]">Loading…</div> : (
                            <RequestList
                                tenant={tenant}
                                requests={requests.filter((r) => {
                                    if (tab === "all") return true;
                                    if (tab === "open") return r.status === "open" || r.status === "in_progress";
                                    return r.status === "completed";
                                })}
                                clientName={clientName}
                                onComplete={(r) => setStatus(r, "completed")}
                                onCancel={(r) => setStatus(r, "cancelled")}
                                onAttached={load}
                            />
                        )}
                    </TabsContent>
                ))}

                <TabsContent value="templates" className="mt-4">
                    <div className="co-card overflow-hidden">
                        <ul className="divide-y divide-[#E4E4E7]" data-testid="templates-list">
                            {templates.length === 0 && <li className="p-4 text-sm text-[#5E636E]">No templates yet.</li>}
                            {templates.map((t) => (
                                <li key={t.id} className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-sm font-medium">{t.name}</div>
                                            <div className="text-xs text-[#5E636E] mt-0.5">{labelFor(REQUEST_CATEGORIES, t.category)} · {t.country}</div>
                                        </div>
                                        <span className="text-[11px] text-[#5E636E]">{(t.doc_types_required || []).length} doc types</span>
                                    </div>
                                    <div className="mt-2 text-xs text-[#5E636E] line-clamp-2">{t.description}</div>
                                    {(t.doc_types_required || []).length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-1">
                                            {t.doc_types_required.map((d) => (
                                                <span key={d} className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px]">{labelFor(DOC_TYPES, d)}</span>
                                            ))}
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}

function RequestList({ requests, clientName, onComplete, onCancel, onAttached, tenant }) {
    if (!requests.length) {
        return <EmptyState icon={ClipboardList} title="No requests here" description="Use the New request button to send one." testId="reqs-empty" />;
    }
    return (
        <div className="co-card overflow-hidden">
            <ul className="divide-y divide-[#E4E4E7]" data-testid="requests-list">
                {requests.map((r) => (
                    <li key={r.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`req-row-${r.id}`}>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <div className="text-sm font-medium truncate">{r.title}</div>
                                <StatusBadge status={r.status} />
                                {r.external_source === "practice_manager" && (
                                    <PracticeManagerLink
                                        url={tenant?.practice_manager_url}
                                        label={tenant?.practice_manager_label}
                                        entityId={r.external_id}
                                        entityKind="job"
                                        testId={`req-pm-link-${r.id}`}
                                    />
                                )}
                                {r.jotform?.status === "awaiting_signature" && (
                                    <a
                                        href={r.jotform.signing_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 px-2 py-0.5 text-[10px] font-medium hover:bg-amber-100"
                                        data-testid={`req-sign-link-${r.id}`}
                                    >
                                        <FileSignature className="h-3 w-3" /> Awaiting signature
                                    </a>
                                )}
                                {r.jotform?.status === "completed" && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-2 py-0.5 text-[10px] font-medium" data-testid={`req-signed-${r.id}`}>
                                        <CheckCircle2 className="h-3 w-3" /> Signed via Jotform
                                    </span>
                                )}
                            </div>
                            <div className="text-xs text-[#5E636E] mt-0.5">{clientName(r.client_id)} · Due {formatDate(r.due_date)} · {r.submission_ids?.length || 0} submissions</div>
                            <div className="text-xs text-[#0F1115] mt-1 line-clamp-2">{r.description}</div>
                        </div>
                        {(r.status === "open" || r.status === "in_progress") && (
                            <div className="flex gap-2">
                                {!r.jotform?.form_id && (
                                    <AttachSignatureButton request={r} onAttached={onAttached} />
                                )}
                                <Button variant="outline" size="sm" onClick={() => onCancel(r)} data-testid={`req-cancel-${r.id}`}>Cancel</Button>
                                <Button size="sm" className="bg-[#171717] hover:bg-[#2E2E2E] text-white" onClick={() => onComplete(r)} data-testid={`req-complete-${r.id}`}>Mark complete</Button>
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}

function NewRequestDialog({ templates, clients, onCreated }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        template_id: "",
        client_id: "",
        title: "",
        description: "",
        doc_types_required: [],
        period_start: "",
        period_end: "",
        due_date: "",
    });
    const [busy, setBusy] = useState(false);
    const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    function applyTemplate(id) {
        const t = templates.find((x) => x.id === id);
        if (!t) return;
        setForm((f) => ({
            ...f,
            template_id: id,
            title: t.name,
            description: t.description,
            doc_types_required: t.doc_types_required || [],
        }));
    }

    function toggleDoc(dt) {
        setForm((f) => ({
            ...f,
            doc_types_required: f.doc_types_required.includes(dt)
                ? f.doc_types_required.filter((x) => x !== dt)
                : [...f.doc_types_required, dt],
        }));
    }

    async function submit() {
        if (!form.client_id || !form.title) return;
        setBusy(true);
        try {
            const payload = { ...form };
            if (!payload.template_id) delete payload.template_id;
            ["period_start", "period_end", "due_date"].forEach((k) => { if (!payload[k]) delete payload[k]; });
            await api.post("/requests/", payload);
            toast.success("Request sent");
            setOpen(false);
            setForm({ template_id: "", client_id: "", title: "", description: "", doc_types_required: [], period_start: "", period_end: "", due_date: "" });
            onCreated?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="bg-[#171717] hover:bg-[#2E2E2E] text-white" data-testid="new-request-btn"><Plus className="h-4 w-4" /> New request</Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl" data-testid="new-request-dialog">
                <DialogHeader><DialogTitle>Send a document request</DialogTitle></DialogHeader>
                <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                    <div>
                        <Label>Use a template (optional)</Label>
                        <Select value={form.template_id} onValueChange={applyTemplate}>
                            <SelectTrigger className="mt-1" data-testid="nr-template"><SelectValue placeholder="Pick a template" /></SelectTrigger>
                            <SelectContent>
                                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Client</Label>
                        <Select value={form.client_id} onValueChange={(v) => s("client_id", v)}>
                            <SelectTrigger className="mt-1" data-testid="nr-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                            <SelectContent>
                                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Title</Label>
                        <Input className="mt-1" value={form.title} onChange={(e) => s("title", e.target.value)} data-testid="nr-title" />
                    </div>
                    <div>
                        <Label>Description (what the client should send and why)</Label>
                        <Textarea rows={3} className="mt-1" value={form.description} onChange={(e) => s("description", e.target.value)} data-testid="nr-desc" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div><Label>Period start</Label><Input type="date" className="mt-1" value={form.period_start} onChange={(e) => s("period_start", e.target.value)} data-testid="nr-period-start" /></div>
                        <div><Label>Period end</Label><Input type="date" className="mt-1" value={form.period_end} onChange={(e) => s("period_end", e.target.value)} data-testid="nr-period-end" /></div>
                        <div><Label>Due date</Label><Input type="date" className="mt-1" value={form.due_date} onChange={(e) => s("due_date", e.target.value)} data-testid="nr-due" /></div>
                    </div>
                    <div>
                        <Label>Documents needed</Label>
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {DOC_TYPES.map((d) => (
                                <label key={d.value} className="flex items-center gap-2 rounded-md border border-[#E4E4E7] bg-white px-3 py-2 text-sm cursor-pointer hover:bg-zinc-50">
                                    <Checkbox
                                        checked={form.doc_types_required.includes(d.value)}
                                        onCheckedChange={() => toggleDoc(d.value)}
                                        data-testid={`nr-doc-${d.value}`}
                                    />
                                    <span>{d.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)} data-testid="nr-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="bg-[#171717] hover:bg-[#2E2E2E] text-white" data-testid="nr-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send request"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function AttachSignatureButton({ request, onAttached }) {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState("sign"); // 'sign' = paste invite URL ; 'form' = pick regular form
    const [forms, setForms] = useState([]);
    const [formId, setFormId] = useState("");
    const [signUrl, setSignUrl] = useState("");
    const [busy, setBusy] = useState(false);
    const [jfStatus, setJfStatus] = useState(null);
    const [webhookInfo, setWebhookInfo] = useState(null);

    useEffect(() => {
        if (!open) return;
        api.get("/integrations/jotform/status").then((r) => {
            setJfStatus(r.data);
            if (r.data.status === "connected") {
                setFormId(r.data.default_form_id || "");
                api.get("/integrations/jotform/forms").then((f) => setForms(f.data || [])).catch(() => {});
                api.get("/integrations/jotform/sign-webhook-url").then((w) => setWebhookInfo(w.data)).catch(() => {});
            }
        }).catch(() => {});
    }, [open]);

    async function submit() {
        if (mode === "sign") {
            if (!signUrl.trim()) { toast.error("Paste the Jotform Sign invite URL"); return; }
        } else if (!formId) { toast.error("Pick a form"); return; }
        setBusy(true);
        try {
            const body = mode === "sign"
                ? { request_id: request.id, sign_url: signUrl.trim() }
                : { request_id: request.id, form_id: formId };
            await api.post("/integrations/jotform/attach", body);
            toast.success("Signature attached — client sees 'Sign now' on their portal");
            setOpen(false);
            setSignUrl("");
            onAttached?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    }

    async function copyWebhook() {
        if (!webhookInfo?.webhook_url) return;
        try { await navigator.clipboard.writeText(webhookInfo.webhook_url); toast.success("Webhook URL copied"); }
        catch { toast.info(webhookInfo.webhook_url); }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid={`req-attach-sign-${request.id}`}>
                    <FileSignature className="h-3.5 w-3.5" /> Attach signature
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Attach a signature to this request</DialogTitle>
                </DialogHeader>
                {jfStatus?.status !== "connected" ? (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="attach-sign-not-connected">
                        Jotform isn&apos;t connected yet. Go to <strong>Settings → Integrations</strong> and connect Jotform first.
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div className="flex gap-1 rounded-md bg-zinc-100 p-1 text-xs" data-testid="attach-sign-tabs">
                            <button
                                type="button"
                                onClick={() => setMode("sign")}
                                className={`flex-1 px-3 py-1.5 rounded ${mode === "sign" ? "bg-white shadow-sm font-medium" : "text-[#5E636E]"}`}
                                data-testid="attach-sign-tab-sign"
                            >
                                Jotform Sign URL
                            </button>
                            <button
                                type="button"
                                onClick={() => setMode("form")}
                                className={`flex-1 px-3 py-1.5 rounded ${mode === "form" ? "bg-white shadow-sm font-medium" : "text-[#5E636E]"}`}
                                data-testid="attach-sign-tab-form"
                            >
                                Regular form
                            </button>
                        </div>

                        {mode === "sign" ? (
                            <div className="space-y-2.5">
                                <p className="text-sm text-[#5E636E]">
                                    In Jotform, open your Sign document → <strong>Share</strong> → <strong>Share with Link</strong> → generate an invite for this client → paste the URL below.
                                </p>
                                <Label className="co-label">Jotform Sign invite URL</Label>
                                <Input
                                    value={signUrl}
                                    onChange={(e) => setSignUrl(e.target.value)}
                                    placeholder="https://www.jotform.com/sign/261607781055055/invite/01ktt3c..."
                                    className="font-mono text-xs"
                                    data-testid="attach-sign-url-input"
                                />
                                {webhookInfo?.webhook_url && (
                                    <div className="mt-2 rounded-md border border-[#E4E4E7] bg-[#FAFAFA] p-3">
                                        <div className="text-[11px] font-medium text-[#0F1115]">First-time setup — webhook URL (one-time, per Sign doc)</div>
                                        <div className="mt-1 text-[11px] text-[#5E636E]">Paste this into your Jotform Sign document → Settings → Integrations → Webhooks. Do this ONCE per document, then signing completion will sync automatically forever.</div>
                                        <div className="mt-2 flex items-center gap-2">
                                            <code className="flex-1 truncate rounded bg-white border border-[#E4E4E7] px-2 py-1 text-[10px] font-mono" data-testid="attach-sign-webhook-url">{webhookInfo.webhook_url}</code>
                                            <Button variant="outline" size="sm" type="button" onClick={copyWebhook} data-testid="attach-sign-copy-webhook">Copy</Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-2.5">
                                <p className="text-sm text-[#5E636E]">
                                    Use this for regular Jotform forms with a signature field (not Jotform Sign documents).
                                </p>
                                <Label className="co-label">Form</Label>
                                <Select value={formId} onValueChange={setFormId}>
                                    <SelectTrigger data-testid="attach-sign-form">
                                        <SelectValue placeholder="Pick a form" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {forms.map((f) => (
                                            <SelectItem key={f.id} value={f.id} data-testid={`attach-sign-pick-${f.id}`}>
                                                {f.title}
                                                <span className="text-[#5E636E] font-mono ml-2 text-[10px]">{f.id}</span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={busy || jfStatus?.status !== "connected"} className="bg-[#171717] hover:bg-[#2E2E2E] text-white" data-testid="attach-sign-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Attach"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
